<template>
  <div class="grid gap-6" :class="outerClass?.value">
    <!-- STEPPER START -->
    <div v-if="!hideStepper.value" class="flex gap-2">
      <button
        v-if="!hideButtons?.value"
        class="h-10 !min-w-0 w-[42px] btn btn-md btn-secondary !px-3"
        type="button"
        :disabled="value == min.value"
        @click="calculate(-1 * stepperStep.value)"
      >
        <div class="w-[18px] px-[3px] py-2 flex-col justify-center items-center gap-2.5 inline-flex">
          <div
            :class="`w-2.5 h-0.5 rounded border border-racq-blue ${value == min.value ? 'bg-racq-form-border' : 'bg-racq-blue'}`"
          ></div>
        </div>
      </button>
      <!-- Setting v-if so it renders after stepper value has been populated -->
      <FormKit
        v-if="loaded"
        ref="stepperEl"
        v-model="stepperValue"
        :value="stepperValue"
        outerClass="md:racq-w-sm w-full"
        :name="name + 'stepper'"
        :type="stepperType?.value ?? 'number'"
        valueFormat="number"
        :unit="stepperUnit?.value"
        :decimals="currencyDecimal?.value ?? 0"
        :validation="validation"
        :validationMessages="validationMessage"
        @focus="editingStepper = true"
        @blur="() => updateValue(stepperEl)"
        @keyup.enter="() => updateValue(stepperEl)"
      />
      <button
        v-if="!hideButtons?.value"
        class="h-10 !min-w-0 w-[42px] btn btn-md btn-secondary !px-3"
        type="button"
        :disabled="value == max.value"
        @click="calculate(stepperStep.value)"
      >
        <div class="w-[18px] h-[18px] p-[3px] justify-center items-center flex">
          <div class="w-3 h-3 relative">
            <div
              :class="`w-3 h-0.5 left-0 top-[5px] absolute rounded border border-racq-blue ${value == max.value ? 'bg-racq-form-border' : 'bg-racq-blue'}`"
            ></div>
            <div
              :class="`w-3 h-0.5 left-[5px] top-3 absolute origin-top-left -rotate-90 rounded border border-racq-blue ${value == max.value ? 'bg-racq-form-border' : 'bg-racq-blue'}`"
            ></div>
          </div>
        </div>
      </button>
    </div>
    <!-- STEPPER END -->
    <!-- SLIDER START -->
    <div v-if="!hideSlider.value" class="md:w-[390px]">
      <FormKit
        ref="sliderEl"
        v-model="sliderValue"
        type="slider"
        outerClass="md:w-[390px] w-full"
        :min="min.value"
        :max="max.value"
        :name="name + 'slider'"
      >
        <template #handleMaxInner>
          <div class="w-full h-full" @mousedown="isDragging = true" @touchstart="isDragging = true"></div>
        </template>
      </FormKit>
      <div class="grid grid-cols-2 text-racq-med-grey text-sm font-body">
        <div>{{ minLabel?.value || min.value }}</div>
        <div class="text-right">{{ maxLabel?.value || max.value }}</div>
      </div>
    </div>
    <!-- SLIDER END -->
    <FormKit v-model="value" type="hidden" :name="name" />
  </div>
</template>
<script setup lang="ts">
import { ISitecoreFormGenericInput } from '@/interfaces';
import { toBool } from '@/utils/convert';
import { FormKit } from '@formkit/vue';
import { Field } from '@sitecore-jss/sitecore-jss-vue';
import { onBeforeUnmount, onMounted, ref, watch, computed } from 'vue';

interface Props extends ISitecoreFormGenericInput {
  hideStepper: Field<boolean>;
  hideSlider: Field<boolean>;
  hideButtons?: Field<boolean>;
  stepperType: Field<string>;
  stepperUnit: Field<string>;
  stepperStep: Field<number>;
  currencyDecimal?: Field<number>;
  min: Field<number>;
  max: Field<number>;
  minLabel?: Field<string>;
  maxLabel?: Field<string>;
  validationRegexErrorMessage?: Field<string>;
}

/** Local state and data properties*/
const value = defineModel<number>();
const stepperValue = ref<number>();
const sliderValue = ref<number>();
const props = defineProps<Props>();
const stepperEl = ref<any>();
const sliderEl = ref<any>();
const loaded = ref(false);
const isDragging = ref(false);
const editingStepper = ref(false);

/** Computed Properties */
const validation = computed(() => {
  let v = `min:${props.min.value}|max:${props.max.value}`;
  if (toBool(props.isRequired?.value)) {
    v += '|required';
  }
  return v;
});

const validationMessage = computed(() => {
  try {
    return JSON.parse(props.validationRegexErrorMessage?.value ?? '{}');
  } catch {
    return {};
  }
});

/** Functions */
function calculate(step: number) {
  const req = Number(value.value || props.min.value) + step;
  if (req > props.max.value) {
    value.value = props.max.value;
  } else if (req < props.min.value) {
    value.value = props.min.value;
  } else {
    value.value = req;
  }
}

function updateValue(el: any) {
  let newVal = Number(el.node.value);
  editingStepper.value = false;
  setValue(newVal);
}

function setValue(newVal: number) {
  if (Number.isNaN(newVal) || newVal < props.min.value || newVal > props.max.value) {
    return;
  }
  stepperValue.value = sliderValue.value = value.value = newVal;
}

/** Lifecycle Hooks */
watch(value, (newVal) => {
  if (stepperValue.value != newVal) stepperValue.value = newVal;
  if (sliderValue.value != newVal) sliderValue.value = newVal;
  if (stepperValue.value && !loaded.value) loaded.value = true;
});

watch(sliderValue, (newValue) => {
  if (!isDragging.value) value.value = newValue;
  stepperValue.value = newValue;
});

const mouseupListener = () => {
  if (isDragging.value) {
    isDragging.value = false;
    updateValue(sliderEl.value);
  }
};

onMounted(() => {
  value.value && setValue(value.value);
  document.addEventListener('mouseup', mouseupListener);
  document.addEventListener('touchend', mouseupListener);
});

onBeforeUnmount(() => {
  document.removeEventListener('mouseup', mouseupListener);
  document.removeEventListener('touchend', mouseupListener);
});
</script>
